import { useEffect, useState, useMemo, useCallback } from "react";
import { api, URL, ApiClientError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import {
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { StatusBadge } from "@/components/ui/status-badge";
import { PageHeader } from "@/components/ui/page-header";
import { SearchInput } from "@/components/ui/search-input";
import { SplitLayout } from "@/components/SplitLayout";
import { URLDetailsPanelContent } from "@/components/URLDetailsPanel";

type SortField =
  | "url"
  | "status"
  | "case"
  | "user"
  | "agency"
  | "date_added"
  | "days_live"
  | "last_checked"
  | "noticed_sent";
type SortDirection = "asc" | "desc" | null;

const PAGE_SIZE = 50;

export function URLList() {
  const { logout } = useAuth();
  const [urls, setUrls] = useState<URL[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sortField, setSortField] = useState<SortField | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>(null);
  const [fromId, setFromId] = useState<string | undefined>(undefined);
  const [paginationHistory, setPaginationHistory] = useState<string[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [totalCount, setTotalCount] = useState<number | undefined>(undefined);
  const [selectedUrl, setSelectedUrl] = useState<URL | null>(null);
  const [isPanelOpen, setIsPanelOpen] = useState(false);

  const loadURLs = useCallback(
    async (currentFromId?: string) => {
      setLoading(true);
      setError(null);
      try {
        // Request enriched data (enrich=true by default, but be explicit)
        // Note: The API will return enriched URLs by default
        const response = await api.urls.list(PAGE_SIZE, currentFromId, true);
        // Handle paginated response
        const isPaginated =
          response && typeof response === "object" && "items" in response;
        const data = isPaginated
          ? (
              response as {
                items: URL[];
                total: number;
                has_next_page: boolean;
              }
            ).items
          : (response as URL[]);
        const paginationData = isPaginated
          ? (response as {
              items: URL[];
              total: number;
              has_next_page: boolean;
            })
          : null;

        setUrls(data);
        if (paginationData) {
          setHasMore(paginationData.has_next_page);
          setTotalCount(paginationData.total);
        } else {
          // Fallback to old logic
          setHasMore(data.length === PAGE_SIZE);
        }
      } catch (err) {
        if (err instanceof ApiClientError) {
          if (err.status === 401) {
            logout();
          } else {
            setError(err.detail || "Failed to load URLs");
          }
        } else {
          setError("Failed to load URLs");
        }
      } finally {
        setLoading(false);
      }
    },
    [logout],
  );

  useEffect(() => {
    loadURLs(fromId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromId]);

  const handleNextPage = () => {
    if (urls.length > 0 && !loading) {
      const lastUrlId = urls[urls.length - 1].id;
      const currentFromId = fromId;
      // Update history first with current fromId, then update fromId
      if (currentFromId !== undefined) {
        setPaginationHistory((prev) => [...prev, currentFromId]);
      }
      setFromId(lastUrlId);
    }
  };

  const handlePreviousPage = () => {
    setPaginationHistory((prev) => {
      if (prev.length > 0) {
        const previousFromId = prev[prev.length - 1];
        setFromId(previousFromId || undefined);
        return prev.slice(0, -1);
      } else {
        // Go back to first page
        setFromId(undefined);
        return [];
      }
    });
  };

  const filteredUrls = useMemo(() => {
    let result = urls;

    if (sortField && sortDirection) {
      result = [...result].sort((a, b) => {
        let aValue: any;
        let bValue: any;

        switch (sortField) {
          case "url":
            aValue = a.url?.toLowerCase() || "";
            bValue = b.url?.toLowerCase() || "";
            break;
          case "status":
            aValue = a.status?.toLowerCase() || "";
            bValue = b.status?.toLowerCase() || "";
            break;
          case "last_checked":
            aValue = a.last_checked ? new Date(a.last_checked).getTime() : 0;
            bValue = b.last_checked ? new Date(b.last_checked).getTime() : 0;
            break;
          case "date_added":
            aValue = a.case_created_at
              ? new Date(a.case_created_at).getTime()
              : 0;
            bValue = b.case_created_at
              ? new Date(b.case_created_at).getTime()
              : 0;
            break;
          case "days_live":
            aValue = a.days_live ?? 0;
            bValue = b.days_live ?? 0;
            break;
          case "case":
            aValue = (a.case_name || "").toLowerCase();
            bValue = (b.case_name || "").toLowerCase();
            break;
          case "user":
            aValue = (a.user_display_name || "").toLowerCase();
            bValue = (b.user_display_name || "").toLowerCase();
            break;
          case "agency":
            aValue = (a.agency_name || "").toLowerCase();
            bValue = (b.agency_name || "").toLowerCase();
            break;
          case "noticed_sent":
            // Placeholder - not in current URL type
            aValue = "";
            bValue = "";
            break;
          default:
            return 0;
        }

        if (aValue < bValue) return sortDirection === "asc" ? -1 : 1;
        if (aValue > bValue) return sortDirection === "asc" ? 1 : -1;
        return 0;
      });
    }

    return result;
  }, [urls, sortField, sortDirection]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      // Cycle through: asc -> desc -> null
      if (sortDirection === "asc") {
        setSortDirection("desc");
      } else if (sortDirection === "desc") {
        setSortField(null);
        setSortDirection(null);
      }
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const getSortIcon = (field: SortField) => {
    if (sortField !== field) {
      return <ArrowUpDown className="h-3 w-3 ml-1 text-muted-foreground" />;
    }
    if (sortDirection === "asc") {
      return <ArrowUp className="h-3 w-3 ml-1 text-foreground" />;
    }
    if (sortDirection === "desc") {
      return <ArrowDown className="h-3 w-3 ml-1 text-foreground" />;
    }
    return <ArrowUpDown className="h-3 w-3 ml-1 text-muted-foreground" />;
  };

  // Calculate current page and page display text
  const currentPage =
    fromId !== undefined
      ? paginationHistory.length + 2
      : paginationHistory.length + 1;

  // Calculate total pages if we have totalCount
  let pageDisplayText: string;
  if (totalCount !== undefined && PAGE_SIZE > 0) {
    const totalPages = Math.ceil(totalCount / PAGE_SIZE);
    pageDisplayText = `${currentPage} of ${totalPages} pages`;
  } else if (hasMore) {
    pageDisplayText = `${currentPage} of ? pages`;
  } else {
    pageDisplayText = `Page ${currentPage}`;
  }

  const panelContent = selectedUrl && <URLDetailsPanelContent url={selectedUrl} />;

  return (
    <SplitLayout
      isPanelOpen={isPanelOpen}
      onPanelClose={() => {
        setIsPanelOpen(false);
        setSelectedUrl(null);
      }}
      panelTitle="URL Details"
      panel={panelContent}
    >
      <div className="flex flex-col min-h-0 flex-1">
      {/* Header - fixed height */}
      <PageHeader
        title="URL Management"
        subtitle="Review and manage detected URLs"
      />

      {error && (
        <div className="bg-destructive/10 border border-destructive/20 text-destructive px-4 py-3 rounded mb-4 flex-shrink-0">
          {error}
        </div>
      )}

      {loading ? (
        <div className="text-center py-8">Loading URLs...</div>
      ) : filteredUrls.length === 0 ? (
        <div className="text-center py-8 text-muted-foreground">
          No URLs found
        </div>
      ) : (
        <div className="flex-1 flex flex-col min-h-0">
          <div className="border rounded-lg overflow-hidden bg-card flex flex-col flex-1 min-h-0">
            {/* Filter section - fixed height */}
            <div className="bg-card flex-shrink-0">
              {/* Search bar */}
              <div className="px-6 py-4">
                <SearchInput placeholder="Search URLs..." />
              </div>

              {/* Filter dropdowns */}
              <div className="px-6 pb-4 flex gap-3 flex-wrap">
                <div className="flex-1 min-w-[120px]">
                  <div className="h-9 w-full bg-muted rounded-md flex items-center justify-between px-3 cursor-pointer hover:bg-muted/80 transition-colors">
                    <span className="text-xs text-muted-foreground">
                      All Statuses
                    </span>
                    <ChevronDown className="h-4 w-4 text-muted-foreground shrink-0" />
                  </div>
                </div>
                <div className="flex-1 min-w-[120px]">
                  <div className="h-9 w-full bg-muted rounded-md flex items-center justify-between px-3 cursor-pointer hover:bg-muted/80 transition-colors">
                    <span className="text-xs text-gray-600">All Agencies</span>
                    <ChevronDown className="h-4 w-4 text-muted-foreground shrink-0" />
                  </div>
                </div>
                <div className="flex-1 min-w-[120px]">
                  <div className="h-9 w-full bg-muted rounded-md flex items-center justify-between px-3 cursor-pointer hover:bg-muted/80 transition-colors">
                    <span className="text-xs text-gray-600">All Cases</span>
                    <ChevronDown className="h-4 w-4 text-muted-foreground shrink-0" />
                  </div>
                </div>
                <div className="flex-1 min-w-[120px]">
                  <div className="h-9 w-full bg-muted rounded-md flex items-center justify-between px-3 cursor-pointer hover:bg-muted/80 transition-colors">
                    <span className="text-xs text-gray-600">All Domains</span>
                    <ChevronDown className="h-4 w-4 text-muted-foreground shrink-0" />
                  </div>
                </div>
                <div className="flex-1 min-w-[120px]">
                  <div className="h-9 w-full bg-muted rounded-md flex items-center justify-between px-3 cursor-pointer hover:bg-muted/80 transition-colors">
                    <span className="text-xs text-gray-600">All Hosting</span>
                    <ChevronDown className="h-4 w-4 text-muted-foreground shrink-0" />
                  </div>
                </div>
                <div className="flex-1 min-w-[120px]">
                  <div className="h-9 w-full bg-muted rounded-md flex items-center justify-between px-3 cursor-pointer hover:bg-muted/80 transition-colors">
                    <span className="text-xs text-gray-600">
                      All Registrars
                    </span>
                    <ChevronDown className="h-4 w-4 text-muted-foreground shrink-0" />
                  </div>
                </div>
                <div className="flex-1 min-w-[120px]">
                  <div className="h-9 w-full bg-muted rounded-md flex items-center justify-between px-3 cursor-pointer hover:bg-muted/80 transition-colors">
                    <span className="text-xs text-gray-600">
                      All Registries
                    </span>
                    <ChevronDown className="h-4 w-4 text-muted-foreground shrink-0" />
                  </div>
                </div>
                <div className="flex-1 min-w-[120px]">
                  <div className="h-9 w-full bg-muted rounded-md flex items-center justify-between px-3 cursor-pointer hover:bg-muted/80 transition-colors">
                    <span className="text-xs text-gray-600">All Payment</span>
                    <ChevronDown className="h-4 w-4 text-muted-foreground shrink-0" />
                  </div>
                </div>
                <div className="flex-1 min-w-[120px]">
                  <div className="h-9 w-full bg-muted rounded-md flex items-center justify-between px-3 cursor-pointer hover:bg-muted/80 transition-colors">
                    <span className="text-xs text-gray-600">All Ad Tech</span>
                    <ChevronDown className="h-4 w-4 text-muted-foreground shrink-0" />
                  </div>
                </div>
                <div className="flex-1 min-w-[120px]">
                  <div className="h-9 w-full bg-muted rounded-md flex items-center justify-between px-3 cursor-pointer hover:bg-muted/80 transition-colors">
                    <span className="text-xs text-gray-600">All ISPs</span>
                    <ChevronDown className="h-4 w-4 text-muted-foreground shrink-0" />
                  </div>
                </div>
              </div>
            </div>

            {/* Separator */}
            <div className="border-t border-border flex-shrink-0"></div>

            {/* Showing count and pagination - fixed height */}
            <div className="px-6 py-2 bg-card flex-shrink-0 flex items-center justify-between">
              <div className="text-xs text-muted-foreground">
                Showing {filteredUrls.length} URL
                {filteredUrls.length !== 1 ? "s" : ""}
              </div>
              <div className="flex items-center gap-2">
                <div className="text-sm text-foreground font-medium px-2 min-w-[80px] text-center">
                  {pageDisplayText}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handlePreviousPage}
                  disabled={
                    fromId === undefined && paginationHistory.length === 0
                  }
                  className="h-7 px-3"
                >
                  <ChevronLeft className="h-4 w-4 mr-1" />
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleNextPage}
                  disabled={!hasMore || loading}
                  className="h-7 px-3"
                >
                  Next
                  <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </div>
            </div>

            {/* Separator */}
            <div className="border-t border-border flex-shrink-0"></div>

            {/* Sticky header + scrollable table body */}
            <div className="flex-1 overflow-auto min-h-0 overscroll-contain">
              <table className="w-full">
                <thead className="bg-muted border-b sticky top-0 z-10">
                  <tr>
                    <th
                      className="px-4 py-1 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider cursor-pointer hover:bg-muted/70 transition-colors"
                      onClick={() => handleSort("url")}
                    >
                      <div className="flex items-center">
                        URL
                        {getSortIcon("url")}
                      </div>
                    </th>
                    <th
                      className="px-4 py-1 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider cursor-pointer hover:bg-muted/70 transition-colors"
                      onClick={() => handleSort("status")}
                    >
                      <div className="flex items-center">
                        STATUS
                        {getSortIcon("status")}
                      </div>
                    </th>
                    <th
                      className="px-4 py-2 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider cursor-pointer hover:bg-muted/70 transition-colors"
                      onClick={() => handleSort("case")}
                    >
                      <div className="flex items-center">
                        CASE
                        {getSortIcon("case")}
                      </div>
                    </th>
                    <th
                      className="px-4 py-2 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider cursor-pointer hover:bg-muted/70 transition-colors"
                      onClick={() => handleSort("user")}
                    >
                      <div className="flex items-center">
                        USER
                        {getSortIcon("user")}
                      </div>
                    </th>
                    <th
                      className="px-4 py-2 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider cursor-pointer hover:bg-muted/70 transition-colors"
                      onClick={() => handleSort("agency")}
                    >
                      <div className="flex items-center">
                        AGENCY
                        {getSortIcon("agency")}
                      </div>
                    </th>
                    <th
                      className="px-4 py-1 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider cursor-pointer hover:bg-muted/70 transition-colors"
                      onClick={() => handleSort("date_added")}
                    >
                      <div className="flex items-center">
                        DATE ADDED
                        {getSortIcon("date_added")}
                      </div>
                    </th>
                    <th
                      className="px-4 py-1 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider cursor-pointer hover:bg-muted/70 transition-colors"
                      onClick={() => handleSort("days_live")}
                    >
                      <div className="flex items-center">
                        DAYS LIVE
                        {getSortIcon("days_live")}
                      </div>
                    </th>
                    <th
                      className="px-4 py-1 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider cursor-pointer hover:bg-muted/70 transition-colors"
                      onClick={() => handleSort("last_checked")}
                    >
                      <div className="flex items-center">
                        LAST CHECKED
                        {getSortIcon("last_checked")}
                      </div>
                    </th>
                    <th
                      className="px-4 py-1 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider cursor-pointer hover:bg-muted/70 transition-colors"
                      onClick={() => handleSort("noticed_sent")}
                    >
                      <div className="flex items-center">
                        NOTICES SENT
                        {getSortIcon("noticed_sent")}
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-card divide-y divide-border">
                  {filteredUrls.map((url) => (
                    <tr
                      key={url.id}
                      className={`hover:bg-muted/50 cursor-pointer transition-colors ${
                        selectedUrl?.id === url.id ? 'bg-blue-50 hover:bg-blue-100' : ''
                      }`}
                      onClick={() => {
                        setSelectedUrl(url);
                        setIsPanelOpen(true);
                      }}
                    >
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="text-xs font-medium text-foreground max-w-md truncate">
                          {url.url}
                        </div>
                      </td>
                      <td className="px-4 py-1 whitespace-nowrap">
                        <StatusBadge status={url.status} />
                      </td>
                      <td className="px-4 py-1 whitespace-nowrap text-xs text-muted-foreground">
                        {url.case_name || "—"}
                      </td>
                      <td className="px-4 py-1 whitespace-nowrap text-xs text-muted-foreground">
                        {url.user_display_name || "—"}
                      </td>
                      <td className="px-4 py-1 whitespace-nowrap text-xs text-muted-foreground">
                        {url.agency_name || "—"}
                      </td>
                      <td className="px-4 py-1 whitespace-nowrap text-xs text-muted-foreground">
                        {url.case_created_at
                          ? new Date(url.case_created_at).toLocaleDateString()
                          : "—"}
                      </td>
                      <td className="px-4 py-1 whitespace-nowrap text-xs text-muted-foreground">
                        {url.days_live !== null && url.days_live !== undefined
                          ? url.days_live
                          : "—"}
                      </td>
                      <td className="px-4 py-1 whitespace-nowrap text-xs text-muted-foreground">
                        {url.last_checked
                          ? new Date(url.last_checked).toLocaleDateString()
                          : "Never"}
                      </td>
                      <td className="px-4 py-1 whitespace-nowrap text-xs text-muted-foreground">
                        —
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      </div>
    </SplitLayout>
  );
}
